import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stars02Icon,
  Building07Icon,
  RefreshCcw01Icon,
  FilterLinesIcon,
  ChevronDownIcon,
} from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { DateRangePicker } from './DateRangePicker'
import { Button } from '@/components/base/buttons'
import { AsyncMultiSelect, type SelectOption } from '@/components/forms/AsyncMultiSelect'
import { usePermissions, useAssistantsByIds, useCustomersByIds } from '@/hooks/api'
import type { StatsFilters, DateRangeValue } from '@/types/dashboard'

export interface DashboardFiltersProps {
  filters: StatsFilters
  onFilterChange: (filters: StatsFilters) => void
  className?: string
}

export function DashboardFilters({
  filters,
  onFilterChange,
  className,
}: DashboardFiltersProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canFilterByCustomer = can('view:customers')

  // State for mobile collapse - default collapsed on mobile
  const [isExpanded, setIsExpanded] = useState(false)

  // State for selected items (with names for display)
  const [selectedAssistants, setSelectedAssistants] = useState<SelectOption[]>([])
  const [selectedCustomers, setSelectedCustomers] = useState<SelectOption[]>([])

  // Fetch selected assistants reactively when filter IDs exist
  const { data: existingAssistants = [] } = useAssistantsByIds(
    filters.assistant_ids || [],
    (filters.assistant_ids?.length || 0) > 0
  )

  // Fetch selected customers reactively when filter IDs exist
  const { data: existingCustomers = [] } = useCustomersByIds(
    filters.customer_ids || [],
    (filters.customer_ids?.length || 0) > 0 && canFilterByCustomer
  )

  // Create stable keys for comparison to avoid infinite loops
  const existingAssistantIds = existingAssistants.map((a) => a.id).join(',')
  const filterAssistantIds = (filters.assistant_ids || []).join(',')
  const existingCustomerIds = existingCustomers.map((c) => c.id).join(',')
  const filterCustomerIds = (filters.customer_ids || []).join(',')

  // Initialize selected assistants from fetched data
  useEffect(() => {
    if (existingAssistants.length > 0) {
      setSelectedAssistants(existingAssistants.map((a) => ({ id: a.id, name: a.name })))
    } else if (!filters.assistant_ids?.length) {
      setSelectedAssistants([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAssistantIds, filterAssistantIds])

  // Initialize selected customers from fetched data
  useEffect(() => {
    if (existingCustomers.length > 0) {
      setSelectedCustomers(existingCustomers.map((c) => ({ id: c.id, name: c.name })))
    } else if (!filters.customer_ids?.length) {
      setSelectedCustomers([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCustomerIds, filterCustomerIds])

  // Date range value derived from filters
  const dateRange: DateRangeValue = useMemo(
    () => ({
      start: filters.start_date,
      end: filters.end_date,
      shortcutLabel: filters.shortcutLabel,
    }),
    [filters.start_date, filters.end_date, filters.shortcutLabel]
  )

  const handleDateRangeChange = useCallback(
    (value: DateRangeValue) => {
      onFilterChange({
        ...filters,
        start_date: value.start,
        end_date: value.end,
        shortcutLabel: value.shortcutLabel,
      })
    },
    [filters, onFilterChange]
  )

  const handleAssistantsChange = useCallback(
    (items: SelectOption[]) => {
      setSelectedAssistants(items)
      onFilterChange({
        ...filters,
        assistant_ids: items.length ? items.map((a) => a.id) : undefined,
      })
    },
    [filters, onFilterChange]
  )

  const handleCustomersChange = useCallback(
    (items: SelectOption[]) => {
      setSelectedCustomers(items)
      onFilterChange({
        ...filters,
        customer_ids: items.length ? items.map((c) => c.id) : undefined,
      })
    },
    [filters, onFilterChange]
  )

  const handleReset = useCallback(() => {
    setSelectedAssistants([])
    setSelectedCustomers([])
    onFilterChange({})
  }, [onFilterChange])

  // Check if there are any active filters
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.start_date ||
          filters.end_date ||
          filters.assistant_ids?.length ||
          filters.customer_ids?.length
      ),
    [filters]
  )

  // Count active filters for badge
  const activeFilterCount = useMemo(() => {
    let count = 0
    if (filters.start_date || filters.end_date) count++
    if (filters.assistant_ids?.length) count++
    if (filters.customer_ids?.length) count++
    return count
  }, [filters])

  return (
    <div
      className={cn(
        'rounded-xl bg-gray-50 dark:bg-gray-800/50',
        className
      )}
    >
      {/* Mobile Toggle Header - Only visible on mobile */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between p-4 md:hidden"
      >
        <div className="flex items-center gap-2">
          <FilterLinesIcon className="size-5 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('resources.dashboard.filters.title')}
          </span>
          {activeFilterCount > 0 && (
            <span className="flex size-5 items-center justify-center rounded-full bg-brand-500 text-xs font-medium text-white">
              {activeFilterCount}
            </span>
          )}
        </div>
        <ChevronDownIcon
          className={cn(
            'size-5 text-gray-500 transition-transform duration-200 dark:text-gray-400',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Filter Content - Collapsible on mobile, always visible on desktop */}
      <div
        className={cn(
          'flex flex-wrap items-end gap-3 overflow-hidden transition-all duration-200 md:max-h-none md:p-4',
          isExpanded ? 'max-h-[500px] px-4 pb-4' : 'max-h-0 md:max-h-none'
        )}
      >
        {/* Date Range Filter */}
        <div className="min-w-[240px] flex-1">
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            label={t('resources.dashboard.filters.date_range')}
            placeholder={t('resources.dashboard.filters.select_date_range')}
            showPresets={true}
          />
        </div>

        {/* Assistant Filter */}
        <div className="min-w-[200px] flex-1">
          <AsyncMultiSelect
            label={t('resources.dashboard.filters.assistants')}
            placeholder={t('resources.dashboard.filters.select_assistants')}
            searchPlaceholder={t('resources.dashboard.filters.search_assistants')}
            searchEndpoint="/assistants"
            value={selectedAssistants}
            onChange={handleAssistantsChange}
            emptyMessage={t('resources.dashboard.filters.no_results')}
            icon={Stars02Icon}
          />
        </div>

        {/* Customer Filter (admin/partner only) */}
        {canFilterByCustomer && (
          <div className="min-w-[200px] flex-1">
            <AsyncMultiSelect
              label={t('resources.dashboard.filters.customers')}
              placeholder={t('resources.dashboard.filters.select_customers')}
              searchPlaceholder={t('resources.dashboard.filters.search_customers')}
              searchEndpoint="/customers"
              value={selectedCustomers}
              onChange={handleCustomersChange}
              emptyMessage={t('resources.dashboard.filters.no_results')}
              icon={Building07Icon}
            />
          </div>
        )}

        {/* Reset Button */}
        {hasActiveFilters && (
          <Button
            color="tertiary"
            size="md"
            onClick={handleReset}
            iconLeading={RefreshCcw01Icon}
            className="shrink-0 self-end"
          >
            {t('resources.generic.clear_filters')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default DashboardFilters
